<template>
  <div class="text-center pa-4">
    <v-dialog
      v-model="dialog"
      width="auto"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      persistent
    >
      <v-card
        v-if="webshop_product"
        :width="$vuetify.breakpoint.smAndDown ? '100%' : 900"
        min-height="500"
        prepend-icon="mdi-update"
        class="pa-4 webshop_modal_card"
      >
        <v-row
          class="pa-4 justify-space-between webshop_modal_card_title shrink"
        >
          <p
            class="px-2 pb-0 mb-0 font-weight-bold text-h2 webshop_modal_card_title_text"
          >
            {{ this.$t("title.confirm_product_purchase") }}
          </p>
          <v-icon x-large @click="closeModal">mdi-close</v-icon>
        </v-row>
        <v-container
          class="d-flex bg-surface-variant pa-4 webshop_modal_content_container"
          fill-height
          style="min-height: inherit"
        >
          <v-row
            class="fill-height grow"
            v-if="busy"
            align="center"
            justify="center"
          >
            <v-col class="text-center">
              <v-progress-circular
                :size="100"
                :width="4"
                class="mb-4"
                color="primary"
                indeterminate
              ></v-progress-circular>
              <div class="display-1">
                {{ this.$t("message.payment_is_being_processed") }}
              </div>
            </v-col>
          </v-row>
          <v-row v-else no-gutters>
            <v-form
              spellcheck="false"
              ref="orderform"
              v-model="valid"
              style="width: 100%; min-width: 0"
            >
              <v-row no-gutters>
                <v-col
                  cols="12"
                  md="6"
                  class="order-2 order-md-1 px-1 webshop_modal_userinput_container"
                >
                  <div
                    class="d-flex align-center webshop_modal_userinput_title"
                  >
                    <v-icon color="orange darken-2" size="45"
                      >mdi-account</v-icon
                    >
                    <p
                      class="ma-0 pl-2 text-h3 font-weight-bold"
                      style="color: #F57C00"
                    >
                      {{ this.$t("title.required_data") }}
                    </p>
                  </div>
                  <div class="d-flex">
                    <v-text-field
                      :label="this.$t('label.first_name')"
                      variant="underlined"
                      v-model="first_name"
                      clearable
                      :rules="this.rules.first_name"
                      class="mx-2"
                      required
                      ref="first_name"
                    ></v-text-field>
                    <v-text-field
                      :label="this.$t('label.last_name')"
                      variant="underlined"
                      v-model="last_name"
                      clearable
                      class="mx-2"
                      required
                      ref="last_name"
                    ></v-text-field>
                  </div>
                  <div v-if="!this.licensePlateHidden" class="d-flex">
                    <v-text-field
                      v-model="license_plate"
                      :rules="
                        this.licensePlateRule(
                          this.webshop_product.license_plate_input_requirement
                        )
                      "
                      validate-on-blur
                      :loading="validatingLicensePlateLoader"
                      @blur="validateLicensePlate(license_plate)"
                      @input="setLicensePlateInvalid()"
                      :success-messages="validatingLicensePlateSuccessMessage"
                      :error-messages="validatingLicensePlateErrorMessage"
                      :messages="validatingLicensePlateLoadingMessage"
                      :append-icon="validatingLicensePlateLoadingIcon"
                      :label="this.$t('label.license_plate')"
                      class="mx-2"
                      ref="license_plate"
                    ></v-text-field>
                  </div>
                  <v-text-field
                    :label="this.$t('label.email')"
                    variant="underlined"
                    v-model="email_address"
                    clearable
                    :rules="this.rules.email_address"
                    class="mx-2"
                    required
                    ref="email_address"
                  ></v-text-field>
                  <div class="d-flex align-center">
                    <v-checkbox
                      v-model="accept_tc"
                      :rules="[accept_tc || '']"
                    ></v-checkbox>
                    <p class="pa-0 ma-0" v-html="terms_and_conditions_text"></p>
                  </div>
                  <p class="text-h3 font-weight-bold">
                    {{ this.$t("thead.total_amount") }}: €{{
                      (webshop_product.product.price_in_cents / 100).toFixed(2)
                    }}
                  </p>
                  <div class="d-flex justify-center justify-lg-start">
                    <v-btn
                      size="x-large"
                      width="200"
                      class="orange darken-2 white--text font-weight-bold webshop_modal_purchase_button"
                      @click="proceedToPayment"
                      :disabled="
                        this.validatingLicensePlateLoader ||
                          !this.licensePlateValid
                      "
                      >{{ this.$t("button.pay") }}</v-btn
                    >
                  </div>
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                  class="order-1 order-md-2 px-1 webshop_modal_productdisplay_container"
                >
                  <div
                    class="d-flex align-center webshop_modal_productdisplay_title"
                  >
                    <v-icon color="orange darken-2" size="45"
                      >mdi-shopping</v-icon
                    >
                    <p
                      class="ma-0 pl-2 text-h3 font-weight-bold"
                      style="color: #F57C00"
                    >
                      {{ this.$t("title.desired_product") }}
                    </p>
                  </div>
                  <WebshopProductDisplay
                    :product_name="webshop_product.display_product_name"
                    :product_description="webshop_product.product.description"
                    :product_price="webshop_product.product.price_in_cents"
                    :product_image="webshop_product.product.image_url"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-row>
        </v-container>
      </v-card>
      <v-card
        v-else
        :width="$vuetify.breakpoint.smAndDown ? '100%' : 600"
        min-height="200"
      >
        <v-card-title
          class="font-weight-bold text-h2 webshop_modal_card_title_text"
        >
          {{ this.$t("title.product_not_found") }}
        </v-card-title>
        <v-card-text>
          {{ this.$t("message.product_not_found") }}
        </v-card-text>
        <v-card-actions
          v-if="this.$store.state.config.webshop_configuration.integrator_url"
        >
          <v-btn
            size="x-large"
            width="200"
            class="orange darken-2 white--text font-weight-bold"
            :href="
              this.$store.state.config.webshop_configuration.integrator_url
            "
          >
            {{ this.$t("button.back_to_webshop") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import WebshopProductDisplay from "@/layouts/layout-default/nonportal/components/webshop/WebshopProductDisplay.vue";
import { webshop_order_create } from "../../../../../mixins/webshop";
import Vue from "vue";

export default {
  components: { WebshopProductDisplay },
  mixins: [webshop_order_create],
  props: {
    webshop_product: {
      type: Object,
      required: false
    },
    authenticated: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    hasCustomer() {
      return !!this.$store.state.user.uuid;
    },
    licensePlateHidden() {
      return this.webshop_product.license_plate_input_requirement === "hidden";
    },
    terms_and_conditions_text() {
      let link = this.$router.resolve({ name: "termsandconditions" }).href;
      return this.$t("linked.terms_and_conditions", {
        link: link
      });
    }
  },
  data() {
    return {
      busy: false,
      valid: false,
      dialog: false,
      first_name: "",
      last_name: "",
      license_plate: "",
      email_address: "",
      accept_tc: false
    };
  },
  expose: ["showModal", "closeModal"],
  methods: {
    fillInUserInformation() {
      if (this.hasCustomer) {
        this.first_name = this.$store.state.user.first_name;
        this.last_name = this.$store.state.user.last_name;
        this.email_address = this.$store.state.user.email;
        if (!this.licensePlateHidden) {
          this.license_plate = this.$store.state.user.license_plate || null;
          if (this.license_plate) {
            this.validateLicensePlate(this.license_plate);
          }
        }
      }
    },
    proceedToPayment() {
      if (!this.$refs.orderform.validate()) {
        return;
      }
      this.startPayment();
    },
    showModal() {
      this.dialog = true;
      Vue.nextTick(this.fillInUserInformation);
    },
    setLicensePlateInvalid() {
      this.licensePlateValid = false;
    },
    closeModal() {
      this.dialog = false;
      this.busy = false;
      if (
        this.$store.state.config.webshop_configuration.mode ===
        "enabled_for_integrator"
      ) {
        window.location.href = this.$store.state.config.webshop_configuration.integrator_url;
      }
    },
    startPayment() {
      this.busy = true;
      let license_plate = null;
      if (!this.licensePlateHidden) {
        license_plate = this.license_plate;
      }

      let body = {
        amount_to_pay_in_cents: this.webshop_product.product.price_in_cents,
        customer: {
          first_name: this.first_name,
          last_name: this.last_name,
          email: this.email_address,
          license_plate: license_plate
        },
        product: {
          uuid: this.webshop_product.uuid,
          name: this.webshop_product.display_product_name,
          description: this.webshop_product.product.description,
          price: this.webshop_product.product.price_in_cents
        }
      };
      this.$store.dispatch("webshop/startPayment", {
        body: body,
        modal: this,
        authenticated: this.authenticated
      });
    }
  }
};
</script>
